<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row">
        <div class="col-md-2 col-4 mt-5">
          <a :href="resolve('site.list')"
             @click="navigate('site.list', $event)"
             v-html="arrowLeftSVG" />
        </div>
        <div class="offset-md-1 col-md-6 col-8 mt-5">
          <h1>My Account</h1>
        </div>
      </div>

      <LoadingWrapper :loading-bool="loading">
        <div class="row mb-5">
          <div class="offset-md-3 col-md-6 mt-5">
            <b-alert variant="success" :show="success" fade>
              Information updated!
            </b-alert>
            <b-alert variant="success" :show="verificationRequestSuccess" fade>
              Email verification requested, please check your email.
            </b-alert>
            <b-alert v-for="err in errors" :key="err" variant="danger" show>
              {{ err }}
            </b-alert>
            <b-form v-if="user" @submit="onSubmit">
              <h3 class="mb-3 mt-3">
                User Details
              </h3>
              <b-form-group label="Full name" label-for="userName">
                <b-form-input
                  id="userName"
                  v-model="user.name"
                  required
                />
              </b-form-group>
              <b-form-group label="Company" label-for="userCompany">
                <b-form-input
                  id="userCompany"
                  v-model="user.company"
                  required
                />
              </b-form-group>
              <b-form-group label="Role" label-for="userRole">
                <b-form-input
                  id="userRole"
                  v-model="user.role"
                />
              </b-form-group>
              <b-form-group label="Company email address" label-for="userEmail">
                <div id="userEmail">
                  {{ user.email }}
                </div>
                <LoadingWrapper :loading-bool="verificationRequesting" spinner-class="mb-4">
                  <button v-if="!user.email_verified" class="btn btn-primary mt-2"
                          :disabled="verificationRequesting || verificationRequestSuccess"
                          @click="requestVerification($event)">
                    Verify Email
                  </button>
                </LoadingWrapper>
              </b-form-group>

              <b-form-group label="Remaining image storage" label-for="userRemainingStorage">
                <div id="userRemainingStorage">
                  {{ formatBytes(user.remaining_storage_bytes) }}
                </div>
              </b-form-group>

              <hr>

              <h3 class="mb-3 mt-5">
                Password Change
              </h3>

              <b-form-group label="Current password" label-for="oldPassword">
                <b-form-input
                  id="oldPassword"
                  v-model="user.old_password"
                  type="password"
                />
              </b-form-group>
              <b-form-group label="New password" label-for="newPassword1">
                <b-form-input
                  id="newPassword1"
                  v-model="user.new_password_1"
                  type="password"
                  :state="passwordValid(user.new_password_1)"
                />
                <b-form-invalid-feedback :state="passwordValid(user.new_password_1)">
                  Your password must be at least 10 characters. Safety first!
                </b-form-invalid-feedback>
                <b-form-invalid-feedback :state="passwordMatch()">
                  These passwords must match.
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group label="Repeat new password" label-for="newPassword2">
                <b-form-input
                  id="newPassword2"
                  v-model="user.new_password_2"
                  type="password"
                  :state="passwordValid(user.new_password_2)"
                />
                <b-form-invalid-feedback :state="passwordValid(user.new_password_2)">
                  Your password must be at least 10 characters. Safety first!
                </b-form-invalid-feedback>
                <b-form-invalid-feedback :state="passwordMatch()">
                  These passwords must match.
                </b-form-invalid-feedback>
              </b-form-group>

              <hr>

              <b-button type="submit" variant="primary" class="mb-3"
                        :pressed="submitting"
                        :disabled="submitting || !formValid()">
                Save Changes
              </b-button>
              <a :href="resolve('user.delete')"
                 @click="navigate('user.delete', $event)">
                Delete my account
              </a>
            </b-form>
          </div>
        </div>
      </LoadingWrapper>
    </div>
    <Footer />
  </div>
</template>

<script>
  import apiMixin from "../../utils/api"
  import Footer from "../navigation/Footer"
  import LoadingWrapper from "../navigation/LoadingWrapper"
  import NavBar from "../navigation/Navbar"
  import octicons from "@primer/octicons"


  const alertFadeDelay = 4
  export default {
    name: 'UserDetails',
    components: {Footer, LoadingWrapper, NavBar},
    mixins: [apiMixin],
    data() {
      return {
        user: {
          email: null,
          company: null,
          role: null,
          name: null,

          old_password: "",
          new_password_1: "",
          new_password_2: ""
        },
        success: false,
        submitting: false,
        verificationRequesting: false,
        verificationRequestSuccess: false,

        arrowLeftSVG: octicons["arrow-left"].toSVG({height: '40px'})
      }
    },
    methods: {
      async requestVerification(event) {
        event.preventDefault()
        this.withLoading(async () => {
          try {
            await this.post('/api/users/me/verification-token/')
            this.verificationRequestSuccess = 3
          } catch (e) {
            this.catchApiError(e, false)
          }
        }, 'verificationRequesting')
      },
      formatBytes(bytes) {
        const mbNum = Math.round(bytes * 100 / 1048576) / 100
        return `${mbNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} MB`
      },
      formValid() {
        if ((this.user.old_password + this.user.new_password_1 + this.user.new_password_2).length === 0) {
          return true
        }

        return this.user.old_password
          && this.passwordValid(this.user.new_password_1)
          && this.passwordValid(this.user.new_password_1)
          && this.passwordMatch()
      },
      passwordValid(pwd) {
        if (!pwd) {
          return null
        }
        return pwd.length >= 10
      },
      passwordMatch() {
        if (!this.user.new_password_1 || !this.user.new_password_2) {
          return true
        }
        return this.user.new_password_1 === this.user.new_password_2
      },

      async load() {
        this.withLoading(async () => {
          try {
            await this.$store.dispatch('user/getDetails', this)
            this.user = {...this.user, ...this.$store.state.user.data}
          } catch (e) {
            this.catchApiError(e)
          }
        })
      },
      async onSubmit(e) {
        e.preventDefault()
        this.withLoading(async () => {
          this.success = false
          this.errors = []

          try {
            let promises = [this.patch("/api/users/me/", this.user)]
            if (this.user.old_password.length && this.formValid()) {
              promises.push(this.put("/api/users/me/password/", {
                old_password: this.user.old_password,
                new_password_1: this.user.new_password_1,
                new_password_2: this.user.new_password_2,
              }))
            }

            await Promise.all(promises)
            this.success = alertFadeDelay
          } catch (err) {
            this.catchApiError(err)
          }
        }, 'submitting')
      }
    }
  }
</script>
